import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Permission, UserRole } from '@/shared/types/permissions.types';

export interface PermissionsState {
  permissions: Permission[];
  userRole: UserRole | null;
  lastSync: number | null;
  isSyncing: boolean;
  error: string | null;
}

const initialState: PermissionsState = {
  permissions: [],
  userRole: null,
  lastSync: null,
  isSyncing: false,
  error: null,
};

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    // Iniciar sincronización de permisos
    syncPermissionsRequest: (state) => {
      state.isSyncing = true;
      state.error = null;
    },
    
    // Sincronización exitosa
    syncPermissionsSuccess: (
      state, 
      action: PayloadAction<{
        permissions: Permission[];
        userRole: UserRole;
        timestamp: number;
      }>
    ) => {
      state.isSyncing = false;
      state.permissions = action.payload.permissions;
      state.userRole = action.payload.userRole;
      state.lastSync = action.payload.timestamp;
      state.error = null;
    },
    
    // Error en sincronización
    syncPermissionsFailure: (state, action: PayloadAction<string>) => {
      state.isSyncing = false;
      state.error = action.payload;
    },
    
    // Actualizar permisos específicos (para tiempo real)
    updatePermissions: (state, action: PayloadAction<Permission[]>) => {
      state.permissions = action.payload;
      state.lastSync = Date.now();
    },
    
    // Actualizar rol de usuario
    updateUserRole: (state, action: PayloadAction<UserRole>) => {
      state.userRole = action.payload;
      state.lastSync = Date.now();
    },
    
    // Limpiar permisos (logout)
    clearPermissions: (state) => {
      state.permissions = [];
      state.userRole = null;
      state.lastSync = null;
      state.isSyncing = false;
      state.error = null;
    },
    
    // Limpiar errores
    clearPermissionsError: (state) => {
      state.error = null;
    },
    
    // Forzar resincronización
    forceResync: (state) => {
      state.lastSync = null;
      state.isSyncing = false;
      state.error = null;
    },
  },
});

export const {
  syncPermissionsRequest,
  syncPermissionsSuccess,
  syncPermissionsFailure,
  updatePermissions,
  updateUserRole,
  clearPermissions,
  clearPermissionsError,
  forceResync,
} = permissionsSlice.actions;

export default permissionsSlice.reducer;
